import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Ticket, BedDouble, Users, User as UserIcon, Crown, Check, Flame, MapPin } from "lucide-react";
import TicketsCloseCountdown from "./TicketsCloseCountdown";
import SalesClosedPanel from "./SalesClosedPanel";
import { useSalesClosed } from "@/lib/salesClosed";

export const TICKETS = [
  {
    slug: "single-day",
    name: "Day Pass",
    sub: "One day only · Friday OR Saturday",
    price: "$375",
    priceNote: "per attendee · pick your day",
    capacity: "Available",
    capacityTone: "available",
    icon: Ticket,
    accent: "#22C55E",
    image: "/estate/pool-lounge.jpeg",
    imageAlt: "The poolside lounge — where in-between conversations happen",
    perks: ["2 presentations that day", "Panel OR mentor session (day-dependent)", "Catered lunch that day", "Estate access during workshop hours"],
    desc: "One full day of the workshop — you pick Friday or Saturday when you register. Everything happening that day is yours.",
    href: "https://buy.stripe.com/7sY00cepN0nja165UpdfG0w",
    requiresDayChoice: true,
  },
  {
    slug: "day-pass",
    name: "Weekend Pass",
    sub: "Workshop access only (no overnight stay)",
    price: "$725",
    priceNote: "per attendee",
    capacity: "Available",
    capacityTone: "available",
    icon: Ticket,
    accent: "#FACC15",
    image: "/estate/indoor-pool.jpeg",
    imageAlt: "The 30+ foot screened pool — where the in-water training happens",
    perks: ["All 4 presentations", "Live panel & open Q&A", "Mentor lunch", "Catered lunches + group dinner"],
    desc: "Full workshop access — every session, the panel, every meal at the estate. Book your own lodging off-property.",
    href: "https://buy.stripe.com/9B69AM6Xlfid3CIgz3dfG0p",
  },
  {
    slug: "mens-bunk",
    name: "Women's Bunk Room",
    sub: "All beds claimed",
    price: "$995",
    priceNote: "sold out",
    capacity: "Sold out",
    badge: "Sold Out",
    icon: Users,
    accent: "#00B4D8",
    image: "/estate/game-pavilion.jpeg",
    imageAlt: "Outdoor pavilion — dinners, panel, games, fireplace",
    perks: ["Full bed", "Shared room · 2 bathrooms", "Overnight stay on-property", "Full workshop access"],
    desc: "All beds in the women's bunk room are claimed. Grab a Weekend Pass and book your own lodging nearby to still be part of the workshop.",
    href: "https://buy.stripe.com/eVq5kw6Xl8TP5KQfuZdfG0v",
    soldOut: true,
  },
  {
    slug: "solo-room",
    name: "Solo Room",
    sub: "Both solo rooms claimed",
    price: "$2,250",
    priceNote: "both rooms gone",
    capacity: "Sold out",
    badge: "Sold Out",
    icon: UserIcon,
    accent: "#FACC15",
    image: "/estate/parkour-ninja.jpeg",
    imageAlt: "On the grounds — parkour, tennis, room to move",
    perks: ["Private room", "1 bed", "On-property", "Full workshop access"],
    desc: "All on-property beds are claimed. Grab a Weekend Pass and book your own lodging nearby — you'll still get the full workshop.",
    href: "https://buy.stripe.com/4gMdR2gxV8TPehm96BdfG0t",
    soldOut: true,
  },
];

const INCLUDED_EVERY_TICKET = [
  "All 4 expert presentations (in-water + business)",
  "Live panel with 6 swim business owners",
  "One-on-one mentor lunch session",
  "Catered lunches + a group dinner at the estate",
  "Optional Saturday Palm Beach outing",
  "Curated swag bag from sponsors",
];

export default function TicketsBlock({ sectionId = "tickets", showEarlyBadge = false }) {
  const salesClosed = useSalesClosed();
  return (
    <section id={sectionId} className="relative bg-[#0A1128] text-white py-20 lg:py-28 overflow-hidden">
      {/* Hero visual band */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 wave-bg opacity-30" />
        <div className="absolute top-10 right-0 w-[40rem] h-[40rem] bg-[#FACC15]/15 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[40rem] h-[40rem] bg-[#0066FF]/15 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        {salesClosed ? (
          <SalesClosedPanel />
        ) : (
          <TicketsGrid showEarlyBadge={showEarlyBadge} />
        )}
      </div>
    </section>
  );
}

function TicketsGrid({ showEarlyBadge }) {
  return (
    <>
        {/* Heading */}
        <div className="max-w-3xl mb-10">
          <div className="inline-flex items-center gap-2 mb-4">
            {showEarlyBadge ? <Flame size={14} className="text-[#FACC15]" /> : <Ticket size={14} className="text-[#FACC15]" />}
            <span className="text-xs font-black tracking-[0.3em] text-[#FACC15] uppercase">
              {showEarlyBadge ? "Members Only · Early Access" : "Tickets Are Live"}
            </span>
          </div>
          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.9] mb-5">
            Pick Your <span className="relative inline-block">
              <span className="relative z-10">Spot.</span>
              <span className="absolute inset-x-0 bottom-1 h-5 bg-[#FACC15] -z-0" />
            </span><br />
            <span className="text-white/70 text-3xl md:text-4xl lg:text-5xl">Once it&apos;s gone, it&apos;s gone.</span>
          </h2>
          <p className="text-lg text-white/70 max-w-2xl">
            <span className="font-black text-white">30 attendees max.</span> Day Passes ($375 · pick Fri or Sat) and Weekend Passes still available — all on-property beds are claimed. When the estate fills, the next workshop is six months away.
          </p>
        </div>

        {/* What's included with EVERY ticket — visual band */}
        <div className="mb-10 bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-2xl p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="shrink-0">
              <div className="text-[10px] font-black uppercase tracking-[0.3em] text-[#FACC15] mb-1">Every Ticket Includes</div>
              <div className="font-display-tight text-xl md:text-2xl text-white leading-tight">The full SSU experience.</div>
            </div>
            <div className="md:flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-x-6 md:gap-y-1.5">
              {INCLUDED_EVERY_TICKET.map((p) => (
                <div key={p} className="flex items-start gap-2 text-sm text-white/85">
                  <Check size={14} className="text-[#FACC15] shrink-0 mt-0.5" />
                  <span>{p}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* GIANT CLOSE COUNTDOWN */}
        <div className="mb-10 md:mb-14">
          <TicketsCloseCountdown />
        </div>

        {/* Ticket grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {TICKETS.map((t) => {
            const Icon = t.icon;
            const urgent = t.capacityTone === "urgent";
            const soldOut = Boolean(t.soldOut);
            return (
              <article
                key={t.slug}
                data-testid={`ticket-card-${t.slug}`}
                data-sold-out={soldOut ? "true" : "false"}
                className={`relative bg-white text-[#0A1128] rounded-2xl overflow-hidden border-2 flex flex-col transition-all duration-300 ${
                  soldOut
                    ? "border-transparent opacity-70 grayscale"
                    : "group border-transparent hover:border-[#FACC15] hover:-translate-y-1 hover:shadow-2xl"
                }`}
              >
                {/* Sold-out diagonal banner */}
                {soldOut && (
                  <div
                    className="absolute top-6 -right-10 z-20 bg-red-600 text-white font-black uppercase tracking-[0.25em] text-xs py-1.5 px-12 rotate-[35deg] shadow-lg pointer-events-none select-none"
                    data-testid={`ticket-sold-out-banner-${t.slug}`}
                  >
                    Sold Out
                  </div>
                )}

                {/* Image header */}
                <div className="relative aspect-[16/10] overflow-hidden bg-[#0A1128]">
                  <img
                    src={t.image}
                    alt={t.imageAlt}
                    className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 ${
                      soldOut ? "" : "group-hover:scale-105"
                    }`}
                  />
                  <div className={`absolute inset-0 ${soldOut ? "bg-[#0A1128]/60" : "bg-gradient-to-t from-[#0A1128]/80 via-[#0A1128]/10 to-transparent"}`} />

                  {/* Top tags */}
                  <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2">
                    <div
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full backdrop-blur-md text-[9px] font-black uppercase tracking-widest"
                      style={{ backgroundColor: soldOut ? "#6B7280" : t.accent, color: "#fff" }}
                    >
                      <Icon size={11} /> {t.badge || "Available"}
                    </div>
                    {urgent && !soldOut && (
                      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500 text-white text-[9px] font-black uppercase tracking-widest animate-pulse">
                        <Flame size={10} /> Going Fast
                      </div>
                    )}
                  </div>

                  {/* Caption */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <h3 className="font-display-tight text-2xl leading-tight">{t.name}</h3>
                    <div className="text-[11px] font-bold uppercase tracking-widest text-white/80 mt-0.5">{t.sub}</div>
                  </div>
                </div>

                {/* Body */}
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className={`font-display text-3xl ${soldOut ? "text-[#6B7280] line-through" : "text-[#0A1128]"}`}>{t.price}</span>
                    {t.priceNote && <span className="text-xs text-[#6B7280]">{t.priceNote}</span>}
                  </div>
                  <div className={`text-[11px] font-black uppercase tracking-widest mb-4 ${
                    soldOut ? "text-red-600" : urgent ? "text-red-600" : "text-[#0066FF]"
                  }`}>
                    {t.capacity}
                  </div>

                  <p className="text-sm text-[#4B5563] leading-relaxed mb-4">{t.desc}</p>

                  <ul className="space-y-1.5 mb-5">
                    {t.perks.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-sm text-[#0A1128]">
                        <Check size={14} className={`shrink-0 mt-0.5 ${soldOut ? "text-[#9CA3AF]" : "text-[#22C55E]"}`} />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>

                  {soldOut ? (
                    <button
                      type="button"
                      disabled
                      aria-disabled="true"
                      data-testid={`ticket-buy-${t.slug}`}
                      className="mt-auto inline-flex items-center justify-center gap-2 w-full px-4 py-3.5 bg-[#9CA3AF] text-white/90 font-black uppercase tracking-wider text-sm rounded-xl cursor-not-allowed"
                    >
                      Sold Out
                    </button>
                  ) : (
                    <Link
                      to={`/register?tier=${t.slug}`}
                      data-testid={`ticket-buy-${t.slug}`}
                      className="mt-auto group/cta inline-flex items-center justify-center gap-2 w-full px-4 py-3.5 bg-[#0A1128] hover:bg-[#0066FF] text-white font-black uppercase tracking-wider text-sm rounded-xl transition-colors"
                    >
                      Reserve Now
                      <ArrowRight size={16} className="group-hover/cta:translate-x-1 transition-transform" />
                    </Link>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        {/* Bottom reassurance */}
        <div className="mt-10 grid sm:grid-cols-3 gap-4 text-center">
          <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4">
            <MapPin size={20} className="text-[#FACC15] mx-auto mb-2" />
            <div className="font-display-tight text-base text-white">Private Estate · West Palm Beach</div>
            <div className="text-xs text-white/55 mt-0.5">Screened pool, pavilion, parkour</div>
          </div>
          <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4">
            <Users size={20} className="text-[#FACC15] mx-auto mb-2" />
            <div className="font-display-tight text-base text-white">30 Attendees Max</div>
            <div className="text-xs text-white/55 mt-0.5">Intentionally intimate. Every face matters.</div>
          </div>
          <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4">
            <BedDouble size={20} className="text-[#FACC15] mx-auto mb-2" />
            <div className="font-display-tight text-base text-white">Stripe Secure Checkout</div>
            <div className="text-xs text-white/55 mt-0.5">Beds are first-come, first-served</div>
          </div>
        </div>

        <p className="text-center text-xs text-white/50 mt-6 max-w-2xl mx-auto">
          The workshop is members-only — you&apos;ll need an active SSU community membership to attend. Questions about lodging, splitting rooms, or accessibility? Reply to your confirmation email and we&apos;ll sort it out.
        </p>
    </>
  );
}
